import React, { useEffect, useRef } from "react";
import SubQuestionTableRow from "./SubQuestionTableRow";
import { topicDictionary } from "../../Util/tileDictionary";

// Helper function to check if data contains specific suppression codes
function hasSuppressionCode(data, code) {
  if (!data) return false;
  
  const checkValue = (v) => {
    if (v === null || v === undefined) return false;
    return String(v).trim() === code;
  };
  
  // For array of rows
  if (Array.isArray(data)) {
    return data.some(row => 
      checkValue(row?.DataValue) || 
      checkValue(row?.DataValueRaw) || 
      checkValue(row?.NPercent)
    );
  }
  
  // For object with tableData property
  if (data.tableData && Array.isArray(data.tableData)) {
    return data.tableData.some(row => 
      checkValue(row?.DataValue) || 
      checkValue(row?.DataValueRaw) || 
      checkValue(row?.NPercent)
    );
  }
  
  return false;
}

const SubQuestionTable = ({
  allLocations,
  comparisonData,
  isPatternsOverTime,
  linkFirstColumn,
  onLocationChange,
  onSubQuestionChange,
  parentLocation,
  question,
  scrollToTableHeader,
  setIsPatternsOverTime,
  subQuestion,
  subQuestionTableData,
  tableName,
  tableHeaderRef,
  showSampleSize,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    // If there is no row data for this table instance,
    // do not attempt to auto-scroll; this avoids odd
    // behavior when rapidly clicking subquestions that
    // have no data.
    const hasRows =
      subQuestionTableData &&
      ((Array.isArray(subQuestionTableData) &&
        subQuestionTableData.length > 0) ||
        (!Array.isArray(subQuestionTableData) &&
          subQuestionTableData.tableData &&
          subQuestionTableData.tableData.length > 0));

    if (!hasRows) return;

    if (scrollToTableHeader && tableHeaderRef && tableHeaderRef.current) {
      const el = tableHeaderRef.current;
      try {
        const rect = el.getBoundingClientRect();
        const viewportHeight =
          typeof window !== "undefined"
            ? window.innerHeight || document.documentElement.clientHeight
            : 0;

        // Only auto-scroll when the header is meaningfully outside
        // the current viewport, to avoid jitter when switching
        // between subquestions while already near the table.
        const margin = 40; // px tolerance at top/bottom
        const aboveViewport = rect.top < margin;
        const belowViewport = rect.bottom > viewportHeight - margin;

        if (aboveViewport || belowViewport) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "start",
          });
        }
      } catch (e) {
        // Fallback: if any measurement fails, do a simple scroll
        tableHeaderRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToTableHeader, tableHeaderRef]);

  const tableRowData =
    subQuestionTableData !== null && !Array.isArray(subQuestionTableData)
      ? subQuestionTableData.tableData
      : subQuestionTableData;

  // Generate a stable key for each row to satisfy React's key requirements.
  // Prefer domain identifiers over array indices to keep keys stable across renders.
  const getRowKey = (row, idx) => {
    if (!row) return `row-${idx}`;
    if (isPatternsOverTime && row.Yr != null) return `yr-${row.Yr}`;
    if (allLocations && (row.LocationAbbr || row.LocationTxt))
      return `loc-${row.LocationAbbr || row.LocationTxt}`;
    if (row.SubQuestionId != null) return `sq-${row.SubQuestionId}`;
    if (row.QuestionId != null && row.SubQuestion)
      return `q${row.QuestionId}-sub-${row.SubQuestion}`;
    return `row-${idx}`;
  };

  return (
    <div>
      {subQuestionTableData !== null &&
      typeof subQuestionTableData === "object" ? (
        <>
          <div
            className="table-responsive"
            role="region"
            aria-labelledby="cap1"
          >
            <table
              ref={ref}
              className="table table-striped table-sm"
              style={{ borderCollapse: "collapse" }}
              role="table"
            >
              <caption id="cap1"></caption>
              <thead>
                {comparisonData && (
                  <tr>
                    <th></th>
                    {comparisonData && comparisonData.length >= 1 && (
                      <>
                        <th
                          className="th"
                          colSpan="3"
                          style={{
                            backgroundColor:
                              topicDictionary[question.TopicId].color,
                            color: "white",
                          }}
                        >
                          {parentLocation.LocationDesc}
                        </th>
                        <th
                          className="th"
                          colSpan="3"
                          style={{
                            backgroundColor:
                              topicDictionary[question.TopicId].color,
                            color: "white",
                            border: "none",
                          }}
                        >
                          {comparisonData[0].LocationTxt}
                        </th>
                      </>
                    )}
                  </tr>
                )}
                <tr>
                  <th
                    className="th__nobackground"
                    style={{
                      color: "#555",
                      fontSize: "25px",
                      width: "40%",
                      borderBottom: "1px solid black",
                    }}
                  >
                    {isPatternsOverTime ? (
                      ""
                    ) : (
                      <span className="m-0">{tableName}</span>
                    )}
                  </th>
                  {/* N column moved to second position and toggle visibility */}
                  {showSampleSize && (
                    <th
                      style={{
                        backgroundColor: topicDictionary[question.TopicId].color,
                        color: "white",
                        border: "none",
                      }}
                      className={comparisonData ? `` : "th"}
                    >
                      N
                    </th>
                  )}
                  <th
                    style={{
                      backgroundColor: topicDictionary[question.TopicId].color,
                      color: "white",
                      border: "none",
                    }}
                    className={comparisonData ? `` : "th"}
                  >
                    %
                  </th>
                  <th
                    style={{
                      backgroundColor: topicDictionary[question.TopicId].color,
                      color: "white",
                      border: "none",
                    }}
                    className={comparisonData ? `` : "th"}
                  >
                    CI
                  </th>
                  {comparisonData && (
                    <>
                      {showSampleSize && <th className={comparisonData ? `` : "th"}>N</th>}
                      <th className={comparisonData ? `` : "th"}>%</th>
                      <th className={comparisonData ? `` : "th"}>CI</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {Array.isArray(subQuestionTableData) ? (
                  tableRowData.map((subQuestionData, i) => (
                    <SubQuestionTableRow
                      allLocations={allLocations}
                      comparisonData={comparisonData}
                      isPatternsOverTime={isPatternsOverTime}
                      showSampleSize={showSampleSize}
                      key={getRowKey(subQuestionData, i)}
                      linkFirstColumn={linkFirstColumn}
                      onLocationChange={onLocationChange}
                      onSubQuestionChange={onSubQuestionChange}
                      setIsPatternsOverTime={setIsPatternsOverTime}
                      subQuestionData={subQuestionData}
                    ></SubQuestionTableRow>
                  ))
                ) : (
                  <>
                    {tableRowData.map((subQuestionData, i) => {
                      return (
                        <SubQuestionTableRow
                          key={getRowKey(subQuestionData, i)}
                          allLocations={allLocations}
                          comparisonData={comparisonData}
                          onLocationChange={onLocationChange}
                          subQuestionData={subQuestionData}
                          showSampleSize={showSampleSize}
                          linkFirstColumn={linkFirstColumn}
                          onSubQuestionChange={onSubQuestionChange}
                          isPatternsOverTime={isPatternsOverTime}
                        ></SubQuestionTableRow>
                      );
                    })}

                    {tableRowData.length > 1 && (
                      <SubQuestionTableRow
                        allLocations={allLocations}
                        comparisonData={comparisonData}
                        isPatternsOverTime={isPatternsOverTime}
                        key={`range-${tableRowData[0]?.LocationType || "all"}`}
                        linkFirstColumn={linkFirstColumn}
                        onLocationChange={onLocationChange}
                        onSubQuestionChange={onSubQuestionChange}
                        printRange={true}
                        showSampleSize={showSampleSize}
                        subQuestionData={subQuestionTableData.medianRangeData.filter(
                          (i) => i.LocationType === tableRowData[0].LocationType
                        )}
                      ></SubQuestionTableRow>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        subQuestion === null &&
        !Array.isArray(subQuestionTableData) &&
        subQuestionTableData &&
        subQuestionTableData.hasOwnProperty("tableData") &&
        subQuestionTableData.tableData.length === 0 && <></>
      )}
    </div>
  );
};

export default SubQuestionTable;
